/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import _sortBy from 'lodash.sortby';
import BaseEditor, { ActionComposerEditorArgs, FieldName } from 'cio/components/action-composer/base-glimmer/editor';
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import ApiService from 'cio/services/api';
import { tracked } from '@glimmer/tracking';
import TemplateModel from 'cio/models/template';
import { isBlank } from '@ember/utils';
import InAppMessageTemplate from 'cio/models/in-app-message-template';
import ArrayProxy from '@ember/array/proxy';
import ApiErrorsService from 'cio/services/api-errors';
import { task, timeout } from 'ember-concurrency';
import InAppMessage from 'cio/app-models/in-app/in-app-message';
import {
  hasAbsoluteDateBeenSelectedOnDifferentTz,
  InAppMessageConfig,
  InAppMessageConfigUpdate,
  MESSAGE_PRIORITY_LABELS,
  pageRuleSummary,
} from 'cio/utils/in-app/message-config';
import Ember from 'ember';
import config from 'cio/config/environment';
import InAppMessageTemplateService from 'cio/services/in-app-message-template';
import { InAppPreview } from 'cio/utils/previews';
import { getLiquidToInsert } from 'cio/utils/liquid';

interface InAppClassicEditorArgs extends ActionComposerEditorArgs {
  template: TemplateModel;
  message: InAppMessage;
  previewDidChange: () => unknown;
  onMessageAttributeChange: (name: string, value: string) => unknown;
  onMessageConfigChange: (config: InAppMessageConfig) => unknown;
  onMessageTemplateChange: (messageId: string, attributes: string[]) => unknown;
  onRecipientChange: (recipient: string) => unknown;
  hasReadOnlyPermission: boolean;
}

interface InAppClassicEditorSignature {
  Args: InAppClassicEditorArgs;
  Element: HTMLDivElement;
}

export default class InAppClassicEditor extends BaseEditor<InAppClassicEditorSignature> {
  @service api!: ApiService;
  @service apiErrors!: ApiErrorsService;
  @service inAppMessageTemplate!: InAppMessageTemplateService;

  @tracked expanded = false;
  @tracked messages: InAppMessageTemplate[] | ArrayProxy<InAppMessageTemplate> = [];
  @tracked showAbsoluteDateTooltip = false;

  interval = config.APP.pollingDelay;

  get hasMessageAttributes() {
    return this.messageAttributes.length > 0;
  }

  get isPageRulesConfigured() {
    return this.message.routeRules.length > 0;
  }

  get expireDate() {
    let { preview } = this.args;
    return (preview as InAppPreview)?.body_json?.expiry;
  }

  get message() {
    return this.args.message;
  }

  get messageAttributes() {
    let { attributes } = this.message;
    return _sortBy(attributes, ['key']);
  }

  get messageDisplayError() {
    let { display } = this.message;
    if (!display.hasErrors) {
      return '';
    }
    return display.errors[0];
  }

  get messageNames() {
    return this.messages.map((m) => ({ label: m.name, value: m.id }));
  }

  get pageRuleSummary() {
    return pageRuleSummary(this.message.routeRules);
  }

  get priorityLabel() {
    return MESSAGE_PRIORITY_LABELS[this.message.priority];
  }

  get selectedMessageId() {
    return this.message.messageId;
  }

  @action
  insertLiquid([fieldName]: [FieldName, number], liquid: string, fallback?: string) {
    if (isBlank(liquid)) {
      // nothing to add
      return;
    }

    let replacement = getLiquidToInsert(liquid, fallback, this.args.editableEntity.template?.templateEngine);

    let el = document.getElementById(`in-app-message-attr-${fieldName}`);

    if (el instanceof HTMLInputElement) {
      let start = el.selectionStart ?? 0;
      let end = el.selectionEnd ?? 0;

      el.setRangeText(replacement, start, end, 'end');
      if (el !== document.activeElement) {
        el.focus();
      }
      // trigger a change event so that the attribute value is updated
      el.dispatchEvent(new Event('change', { bubbles: true }));
    } else {
      // fall back to notifying a change in the attribute if the element was not found
      this.args.onMessageAttributeChange(fieldName, replacement);
    }
  }

  pollForMessageList = task({ drop: true }, async () => {
    // eslint-disable-next-line no-constant-condition
    while (true) {
      await this.fetchMessages();
      this.updateSelectedMessage();

      if (Ember.testing) {
        break;
      }
      await timeout(this.interval);
    }
  });

  async fetchMessages() {
    try {
      this.messages = await this.inAppMessageTemplate.loadAllMessageTemplates();
    } catch (e) {
      this.apiErrors.report(e);
    }
  }

  @action
  updateSelectedMessage() {
    let { messageId } = this.message;
    if (this.message.isMessageIdPresent && this.messageStillExistsAndPropertiesHaveChanged(messageId)) {
      this.selectMessage(messageId);
    }
  }

  private messageStillExistsAndPropertiesHaveChanged(messageId: string) {
    let messageTemplate = this.messages.find((item) => item.id === messageId);
    if (messageTemplate == null) {
      return false;
    }
    let existingAttributes = new Set(this.message.attributes.map((attr) => attr.key));
    return (
      messageTemplate.properties.length !== this.message.attributes.length ||
      messageTemplate.properties.some((attr) => !existingAttributes.has(attr))
    );
  }

  @action
  toggleExpanded() {
    this.expanded = !this.expanded;
  }

  @action
  onMessageSelect(selected: { value: string; label: string }) {
    this.selectMessage(selected.value);
  }

  selectMessage(messageId: string) {
    let attributes = this.getMessagePropertyList(messageId) ?? [];
    this.args.onMessageTemplateChange(messageId, attributes);
  }

  @action
  onInput(name: string, value: string) {
    this.args.onMessageAttributeChange(name, value);
  }

  @action
  onMessageConfigurationChange(config: InAppMessageConfigUpdate) {
    let { expiration, display, priority, routeRules, options, persistent } = config;

    this.showAbsoluteDateTooltip = hasAbsoluteDateBeenSelectedOnDifferentTz(options);

    this.args.onMessageConfigChange({ expiration, display, priority, routeRules, persistent });
  }

  getMessagePropertyList(messageId: string) {
    return this.messages.find((item) => item.id === messageId)?.properties;
  }

  @action
  setRecipient(recipient: string) {
    this.args.onRecipientChange(recipient);
  }
}
